import React from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Avatar,
  Chip,
  Divider,
} from "@mui/material";
import {
  Close as CloseIcon,
  Favorite as FavoriteIcon,
  Bookmark as BookmarkIcon,
  Share as ShareIcon,
  AccessTime as TimeIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { IArticleViewDialogProps } from "../types/article-view";

const ArticleViewDialog: React.FC<IArticleViewDialogProps> = ({
  open,
  onClose,
  article,
}) => {
  if (!article) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      component={motion.div}
    >
      <DialogContent sx={{ p: 0, maxHeight: "80vh", overflow: "auto" }}>
        <Box sx={{ position: "relative" }}>
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              bgcolor: "rgba(255, 255, 255, 0.9)",
              zIndex: 1,
              "&:hover": { bgcolor: "rgba(255, 255, 255, 1)" },
            }}
          >
            <CloseIcon />
          </IconButton>

          <Box sx={{ p: 4 }}>
            {/* Article Header */}
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              fontWeight="bold"
            >
              {article.title}
            </Typography>

            <Typography variant="h6" color="text.secondary" paragraph>
              {article.description}
            </Typography>

            {/* Author and Meta Info */}
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Avatar sx={{ width: 40, height: 40, mr: 2 }}>
                {typeof article.author === "string" && article.author.length > 0
                  ? article.author.charAt(0).toUpperCase()
                  : "S"}
              </Avatar>

              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {article.author}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {article.datePublished}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <TimeIcon sx={{ fontSize: 16 }} />
                    <Typography variant="body2" color="text.secondary">
                      {article.readTime}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: "flex", gap: 1 }}>
                <IconButton>
                  <BookmarkIcon />
                </IconButton>
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Featured Image */}
            <Box sx={{ mb: 4 }}>
              <img
                src={article.image || "/placeholder.svg"}
                alt={article.title}
                style={{
                  width: "100%",
                  height: "300px",
                  objectFit: "cover",
                  borderRadius: "8px",
                }}
              />
            </Box>

            {/* Article Content */}
            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              In the rapidly evolving world of technology, staying ahead of
              design trends is crucial for creating compelling user experiences.
              As we move through 2024, several key trends are reshaping how we
              approach UI/UX design, from the integration of artificial
              intelligence to the growing emphasis on accessibility and
              inclusive design.
            </Typography>

            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              sx={{ mt: 4, mb: 2 }}
            >
              The Rise of AI-Powered Interfaces
            </Typography>

            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Artificial intelligence is no longer a futuristic concept—it's
              becoming an integral part of modern user interfaces. AI-powered
              design tools are enabling designers to create more personalized
              and adaptive experiences. From predictive text and smart
              suggestions to automated layout generation, AI is transforming how
              users interact with digital products.
            </Typography>

            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Machine learning algorithms can now analyze user behavior patterns
              to optimize interface elements in real-time, creating truly
              dynamic user experiences that adapt to individual preferences and
              usage patterns.
            </Typography>

            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              sx={{ mt: 4, mb: 2 }}
            >
              Immersive Technologies and Spatial Design
            </Typography>

            <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
              Virtual and augmented reality technologies are moving beyond
              gaming and entertainment into mainstream applications. This shift
              is creating new opportunities for spatial design, where
              traditional 2D interfaces give way to three-dimensional, immersive
              experiences.
            </Typography>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ArticleViewDialog;
